// ============================================================
// FICHIER : boutique/toilesia-identite.js
// RÔLE    : Lier automatiquement le token de session (localStorage)
//           au client Shopify connecté (window.myCustomer.id) sur
//           N'IMPORTE QUELLE page, via POST /session/connect.
//           Autonome : s'appuie sur Toilesia.config s'il est chargé,
//           sinon sur les valeurs de repli ci-dessous. Injecte
//           auth-client.js à la demande s'il est absent du thème.
// ⚠️ auth-client.js est REQUIS : l'API refuse toute requête sans
//    les en-têtes de sécurité client X-Auth-* (403).
// Chargement : voir shopify/toilesia-identite.liquid (theme.liquid).
// ============================================================

window.ToilesiaIdentite = (function () {
    'use strict';

    // Valeurs de repli si config.js n'est pas chargé sur la page.
    var API_URL = (window.Toilesia && Toilesia.config && Toilesia.config.apiUrl)
        || 'https://api.auclairdestoiles.fr';
    var CLE_SESSION = (window.Toilesia && Toilesia.config && Toilesia.config.cleSession)
        || 'toilesia_session';

    var _scriptCourant = document.currentScript; // lisible pendant l'exécution synchrone
    var _authEnCours = null;

    // ------------------------------------------------------------
    // Lecture du token de session (même clé localStorage que le widget)
    // ------------------------------------------------------------
    function jetonSession() {
        try {
            return window.localStorage.getItem(CLE_SESSION) || '';
        } catch (e) {
            return '';
        }
    }

    // ------------------------------------------------------------
    // Identifiant Shopify du client connecté ({{ customer.id }})
    // ------------------------------------------------------------
    function customerId() {
        if (window.myCustomer && window.myCustomer.id) {
            return String(window.myCustomer.id);
        }
        return '';
    }

    // ------------------------------------------------------------
    // Résolution d'un fichier voisin (même dossier que ce script),
    // en conservant la query « ?v=… » de Shopify (cache CDN).
    // ------------------------------------------------------------
    function urlVoisine(nomFichier) {
        if (_scriptCourant && _scriptCourant.src) {
            var src = _scriptCourant.src;
            var i = src.indexOf('?');
            var chemin = (i === -1) ? src : src.slice(0, i);
            return chemin.replace(/[^/]*$/, nomFichier) + (i === -1 ? '' : src.slice(i));
        }
        return nomFichier;
    }

    function authDisponible() {
        return !!(window.ToilesiaAuth && typeof window.ToilesiaAuth.apiCall === 'function');
    }

    function scriptAuthExistant() {
        var scripts = document.getElementsByTagName('script');
        for (var i = 0; i < scripts.length; i++) {
            if (/auth-client\.js(\?|$)/.test(scripts[i].src || '')) return scripts[i];
        }
        return null;
    }

    /**
     * Garantit la disponibilité de auth-client.js (ToilesiaAuth) : déjà
     * chargé par le thème (recommandé) ou injecté ici à la demande, une
     * seule fois. Sans lui, impossible de fabriquer les en-têtes X-Auth-*.
     * @returns {Promise<boolean>}
     */
    function garantirAuth() {
        if (authDisponible()) return Promise.resolve(true);
        if (_authEnCours) return _authEnCours;

        _authEnCours = new Promise(function (resolve) {
            if (scriptAuthExistant()) {
                var essais = 0;
                (function attendre() {
                    if (authDisponible()) return resolve(true);
                    if (++essais > 40) { // ~2 s
                        console.error('[ToilesiaIdentite] auth-client.js chargé mais inutilisable.');
                        return resolve(false);
                    }
                    setTimeout(attendre, 50);
                })();
                return;
            }

            var script = document.createElement('script');
            script.src = urlVoisine('auth-client.js');
            script.async = true;
            script.onload = function () { resolve(authDisponible()); };
            script.onerror = function () { resolve(false); };
            document.head.appendChild(script);
        });
        return _authEnCours;
    }

    var _sessionEnCours = null; // verrou anti-doublon de création de session

    // ------------------------------------------------------------
    // Création d'une session (GET /session/new) quand le client est
    // connecté mais n'a pas encore de token (nouveau navigateur,
    // stockage vidé, widget jamais ouvert…). Le token est stocké sous
    // la même clé localStorage que le widget pour les pages suivantes.
    // ------------------------------------------------------------
    function creerSession() {
        if (_sessionEnCours) return _sessionEnCours;

        // Verrou « single-flight » PARTAGÉ avec le widget (modules/session.js) :
        // une seule création de session en vol, même si les deux modules
        // s'initialisent en parallèle sur une page produit (sinon 2 sessions
        // + 2 utilisateurs anonymes + risque 429 sur /session/new).
        window.Toilesia = window.Toilesia || {};
        if (!window.Toilesia.__sessionEnCours) {
            window.Toilesia.__sessionEnCours = window.ToilesiaAuth.apiCall('session/new', 'GET')
                .then(function (reponse) {
                    if (reponse && reponse.succes && reponse.session_id) {
                        return String(reponse.session_id);
                    }
                    throw new Error((reponse && reponse.erreur) || 'Échec de création de session');
                })
                .finally(function () {
                    window.Toilesia.__sessionEnCours = null;
                });
        }

        _sessionEnCours = window.Toilesia.__sessionEnCours.then(function (token) {
            if (token) {
                try {
                    window.localStorage.setItem(CLE_SESSION, token);
                } catch (e) {
                    // stockage indisponible (navigation privée restreinte, etc.)
                }
            }
            return token;
        }).catch(function () {
            return '';
        }).then(function (token) {
            _sessionEnCours = null; // libère le verrou pour un éventuel nouvel essai
            return token;
        });

        return _sessionEnCours;
    }

    // ------------------------------------------------------------
    // Liaison : token de session + customer.id → POST /session/connect
    // Silencieuse (aucune notification) : simple rattachement comptable.
    // Idempotente : relancer la liaison ne crée pas de doublon.
    // ------------------------------------------------------------
    function posterLiaison(cid, token) {
        return window.ToilesiaAuth.apiCall(
            'session/connect',
            'POST',
            { customer_id: cid },
            { 'X-Session-Token': token }
        ).then(function (reponse) {
            if (!(reponse && reponse.succes)) {
                console.warn('[ToilesiaIdentite] Échec liaison session/connect :', (reponse && reponse.erreur) || 'réponse invalide');
            }
            return reponse;
        });
    }

    function supprimerToken() {
        try {
            window.localStorage.removeItem(CLE_SESSION);
        } catch (e) {
            // stockage indisponible (navigation privée restreinte, etc.)
        }
    }

    // Tente la liaison avec un token donné. Si la session est introuvable
    // (404 « Session introuvable »), le token en mémoire est obsolète
    // (ex. base réinitialisée, session purgée…) : on le purge, on en crée
    // un neuf et on réessaie une seule fois.
    function lierAvecToken(cid, token) {
        var pret = token ? Promise.resolve(token) : creerSession();

        return pret.then(function (tok) {
            if (!tok) {
                console.warn('[ToilesiaIdentite] Aucune session (ni existante ni créée) — liaison impossible.');
                return false;
            }
            return posterLiaison(cid, tok).then(function (reponse) {
                if (reponse && reponse.succes) return true;

                // Session obsolète côté serveur : on en recrée une fraîche.
                if (reponse && reponse.statut_http === 404 && token) {
                    supprimerToken();
                    return creerSession().then(function (nouveau) {
                        if (!nouveau) return false;
                        return posterLiaison(cid, nouveau).then(function (r2) {
                            return !!(r2 && r2.succes);
                        });
                    });
                }
                return false;
            });
        });
    }

    function lierSession() {
        var cid = customerId();
        if (!cid) return Promise.resolve(false);

        var jeton = jetonSession();

        return garantirAuth().then(function (authPrete) {
            if (!authPrete) {
                console.warn('[ToilesiaIdentite] auth-client.js indisponible — liaison annulée.');
                return false;
            }
            return lierAvecToken(cid, jeton);
        }).catch(function (e) {
            console.warn('[ToilesiaIdentite] Erreur de liaison :', (e && e.message) ? e.message : e);
            return false;
        });
    }

    function demarrer() {
        // Déclenché sur toutes les pages : le token existe généralement
        // après une visite sur une page produit, et le client peut être
        // connecté ailleurs (compte, panier…). On réessaie à intervalle
        // court pour couvrir les connexions tardives (drawer / app shell).
        lierSession();
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', lierSession);
        }
        window.setTimeout(lierSession, 1500);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', demarrer);
    } else {
        demarrer();
    }

    return {
        lierSession: lierSession
    };
})();
